/**
 * Help for Advanced mode: what each control does (every icon with its name,
 * for a collapsed row), how the 3D view is driven, and the keyboard.
 */

import React from 'react';
import {
  Box, CircleQuestionMark, Layers, LogOut, Maximize, MousePointer2, PencilRuler, RotateCcw, Sofa, Undo2, Warehouse,
} from 'lucide-react';
import { DesignerModal } from '../chrome/DesignerModal';
import { DESIGNER_STRINGS as S } from '../strings';

export interface DesignerHelpModalProps {
  onClose: () => void;
  /** Whether the Draw tool is offered (several rooms per blueprint). */
  drawTool: boolean;
}

const Row: React.FC<{ icon?: React.ReactNode; name: string; children: React.ReactNode }> = ({ icon, name, children }) => (
  <div style={{ display: 'flex', gap: 10, padding: '5px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
    <span style={{ flex: '0 0 22px', display: 'flex', justifyContent: 'center', paddingTop: 2, color: '#FEB100' }}>{icon}</span>
    <span style={{ flex: '1 1 auto' }}>
      <strong>{name}</strong>
      <span style={{ display: 'block', color: '#c9cbd3' }}>{children}</span>
    </span>
  </div>
);

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ margin: '14px 0 4px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#feb100' }}>{children}</p>
);

export const DesignerHelpModal: React.FC<DesignerHelpModalProps> = ({ onClose, drawTool }) => {
  const H = S.help;
  const P = S.planner;
  return (
    <DesignerModal title={H.title} onClose={onClose} width={460} icon={<CircleQuestionMark size={16} color="#FEB100" />}>
      <p>{H.intro}</p>
      <Heading>{H.plannerHeading}</Heading>
      <Row icon={<MousePointer2 size={14} />} name={P.select}>{H.select}</Row>
      {drawTool && <Row icon={<PencilRuler size={14} />} name={P.draw}>{H.draw}</Row>}
      <Row icon={<Maximize size={14} />} name={P.fit}>{H.fit}</Row>
      <Row icon={<RotateCcw size={14} />} name={P.reset}>{H.reset}</Row>
      <Row icon={<Warehouse size={14} />} name={S.spaceType}>{H.space}</Row>
      <Row icon={<Layers size={14} />} name={S.rooms.title}>{H.rooms}</Row>
      <Row icon={<Undo2 size={14} />} name={`${S.undoLabel} / ${S.redoLabel}`}>{H.undo}</Row>
      <Row icon={<LogOut size={14} />} name={P.exit}>{H.exit}</Row>
      <Heading>{H.roomHeading}</Heading>
      <p>{H.room}</p>
      <Row icon={<Box size={14} />} name={P.designSpace}>{H.designSpace}</Row>
      <Heading>{H.threeDHeading}</Heading>
      <p>{H.threeD}</p>
      <Row icon={<Sofa size={14} />} name={S.stage}>{H.stage}</Row>
      <Heading>{H.keysHeading}</Heading>
      {H.keys.map(([key, what]) => (
        <Row key={key} icon={<kbd style={{ fontSize: 10, padding: '1px 4px', borderRadius: 4, background: 'rgba(255,255,255,0.12)', color: '#fff' }}>{key}</kbd>} name="">{what}</Row>
      ))}
      <div className="dh-modal-actions">
        <button type="button" className="dh-btn is-accent" onClick={onClose}>{H.done}</button>
      </div>
    </DesignerModal>
  );
};

export default DesignerHelpModal;
